import {
  IconsContainer,
  OfferTag,
  ProductsList,
  StyledBundleProductCard,
} from '@factories/RowFactory/components/BundleProducts/styled/StyledBundleProducts';
import { fromMarkdown, getMonthlyPriceCalculated, getProductPromotion } from '@lib/utils';
import Image from 'next/image';
import { BundleProductT } from '@factories/RowFactory/components/BundleProducts/types';
import React, { useCallback, useMemo } from 'react';
import { ProductT, PromoT } from '@lib/types';
import { Button } from '@components/Atoms';
import { ButtonType } from '@components/Atoms/Button/types';
import useBasketContext from '@hooks/useBasketContext';

export const BundleProductCard = ({
  item,
  products,
  promotions,
  coverage,
}: {
  item: BundleProductT;
  products?: ProductT[];
  promotions: PromoT[];
  coverage?: string;
}) => {
  const { clearBasket, setSelectedItem, setAddons, addons, setBundle, setBundleId, bundleId } =
    useBasketContext();

  const isSelected = bundleId === item.id;

  const bundleTotalPrice = useMemo(() => {
    if (!products || !item.products?.length) return 0;

    return item.products.reduce((total, bundleProduct) => {
      const fullProduct = products.find(
        (product) => product.id_product === bundleProduct.productId,
      );

      const promo = getProductPromotion({
        id_product: fullProduct?.id_product,
        coverage,
        promotions,
      });

      const deductablePromo = Array.isArray(promo)
        ? promo.find(
            ({ promotion_type }) =>
              promotion_type === 'RECURAMTOFF' || promotion_type === 'RECUR%OFF',
          )
        : promo;

      const monthlyPrice = getMonthlyPriceCalculated({
        monthly_price: fullProduct?.monthly_price,
        exclude_vat_price: fullProduct?.exclude_vat_price,
        promo: deductablePromo,
      });

      return total + parseFloat(monthlyPrice as string);
    }, 0);
  }, [products, item.products, coverage, promotions]);

  const addProductsToBasket = useCallback(() => {
    if (isSelected) {
      clearBasket();
      return;
    }

    clearBasket();
    setBundleId(item.id);
    setBundle(true);

    const newAddons = [...addons];

    item.products.map((bundleProduct) => {
      const fullProduct = products?.find(
        (product) => product.id_product === bundleProduct.productId,
      );

      if (fullProduct?.product_type === 'PRODUCT') {
        setSelectedItem(fullProduct.id_product);
      } else if (fullProduct?.product_type === 'ADDON') {
        if (!newAddons.includes(fullProduct.id_product)) {
          newAddons.push(fullProduct.id_product);
        }
      }
    });

    setAddons(newAddons);
  }, [
    isSelected,
    clearBasket,
    setBundleId,
    item.id,
    item.products,
    setBundle,
    addons,
    setAddons,
    products,
    setSelectedItem,
  ]);

  if (!item.display) return null;

  return (
    <StyledBundleProductCard $isSelected={isSelected} onClick={addProductsToBasket}>
      <div className="flex justify-center w-full">
        {item.offer && <OfferTag>{item.offer}</OfferTag>}
      </div>

      <div className="card-header">
        <h3>{item.title}</h3>

        {bundleTotalPrice > 0 && (
          <div className="bundle-price">
            <div>
              <strong>£{bundleTotalPrice}</strong>
              <span> per month</span>
            </div>
          </div>
        )}

        <div className="header-description">
          {fromMarkdown({ text: item.description, isHeading: false })}
        </div>
      </div>

      <div className="card-body">
        {item.details && (
          <>
            <p className="title-section">Bundle includes:</p>
            <ProductsList>{fromMarkdown({ text: item.details, isHeading: false })}</ProductsList>
          </>
        )}

        {item.icons && item.icons.length > 0 && (
          <>
            <p className="title-section">Ideal for:</p>
            <IconsContainer>
              {item.icons.map((icon, iconIndex) => (
                <div key={`icon-container-${iconIndex}`} className="icon-wrapper">
                  <Image
                    key={`icon-${iconIndex}`}
                    src={icon.url as string}
                    alt={icon.alternativeText || ''}
                    width={60}
                    height={60}
                    objectFit="contain"
                  />
                </div>
              ))}
            </IconsContainer>
          </>
        )}
      </div>
      <div className="card-footer">
        <Button buttonType={ButtonType.SIMPLEORANGE}>{isSelected ? 'Selected' : 'Buy Now'}</Button>
      </div>
    </StyledBundleProductCard>
  );
};

export default BundleProductCard;
